/// <reference types="react" />
import React from 'react';
import './or-tabs.css';
import { useOrTabs } from './or-tabs.hook';

export type OrTabsVariant = 'default' | 'with-icons';
export type OrTabsOptionKey = string | number;

export interface OrTabsProps<TOption> {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  errorText?: React.ReactNode;
  fullWidth?: boolean;
  getOptionDisabled?: (option: TOption) => boolean;
  getOptionIcon?: (option: TOption, isActive: boolean) => React.ReactNode;
  getOptionKey?: (option: TOption, index: number) => OrTabsOptionKey;
  getOptionLabel: (option: TOption) => string;
  getPanelId?: (option: TOption, index: number) => string;
  hasError?: boolean;
  helperText?: React.ReactNode;
  id?: string;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  label?: React.ReactNode;
  onChange?: (nextValue: TOption) => void;
  options: readonly TOption[];
  value?: TOption | null;
  variant?: OrTabsVariant;
}

export function OrTabs<TOption>({
  ariaLabel,
  className,
  disabled = false,
  errorText,
  fullWidth = true,
  getOptionDisabled,
  getOptionIcon,
  getOptionKey,
  getOptionLabel,
  getPanelId,
  hasError = false,
  helperText,
  id,
  isOptionEqualToValue,
  label,
  onChange,
  options,
  value,
  variant = 'default',
}: OrTabsProps<TOption>): React.ReactElement {
  const reactId = React.useId();
  const baseId = React.useMemo(() => id ?? `or-tabs-${reactId.replace(/:/g, '')}`, [id, reactId]);
  const labelId = `${baseId}-label`;
  const messageId = `${baseId}-message`;

  const {
    activeIndex,
    focusableIndex,
    handleTabClick,
    handleTabKeyDown,
    handleViewportPointerCancel,
    handleViewportPointerDown,
    handleViewportPointerMove,
    handleViewportPointerUp,
    indicatorStyle,
    isOptionDisabled,
    listClassName,
    listRef,
    setTabRef,
    shouldShowIndicator,
    tabClassName,
    viewportClassName,
    viewportRef,
    wrapperClassName,
  } = useOrTabs({
    className,
    disabled,
    fullWidth,
    getOptionDisabled,
    isOptionEqualToValue,
    onChange,
    options,
    value,
    variant,
  });

  const describedBy = hasError && errorText ? messageId : !hasError && helperText ? messageId : undefined;

  return (
    <div className={wrapperClassName}>
      {label ? (
        <p className="or-tabs__label" id={labelId}>
          {label}
        </p>
      ) : null}

      <div
        className={viewportClassName}
        onPointerCancel={handleViewportPointerCancel}
        onPointerDown={handleViewportPointerDown}
        onPointerMove={handleViewportPointerMove}
        onPointerUp={handleViewportPointerUp}
        ref={(node) => {
          viewportRef.current = node;
        }}
      >
        <div
          aria-describedby={describedBy}
          aria-disabled={disabled}
          aria-invalid={hasError}
          aria-label={label ? undefined : ariaLabel}
          aria-labelledby={label ? labelId : undefined}
          aria-orientation="horizontal"
          className={listClassName}
          ref={(node) => {
          listRef.current = node;
        }}
          role="tablist"
        >
          {shouldShowIndicator ? <span aria-hidden="true" className="or-tabs__indicator" style={indicatorStyle} /> : null}

          {options.map((option, index) => {
            const isActive = index === activeIndex;
            const isDisabledOption = isOptionDisabled(option);
            const optionIcon = getOptionIcon?.(option, isActive);
            const tabId = `${baseId}-tab-${index}`;

            return (
              <button
                aria-controls={getPanelId ? getPanelId(option, index) : undefined}
                aria-selected={isActive}
                className={tabClassName(isActive, isDisabledOption)}
                disabled={isDisabledOption}
                id={tabId}
                key={getOptionKey ? getOptionKey(option, index) : `${getOptionLabel(option)}-${index}`}
                onClick={() => handleTabClick(index)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                ref={(node) => setTabRef(index, node)}
                role="tab"
                tabIndex={focusableIndex === index ? 0 : -1}
                type="button"
              >
                {optionIcon ? <span className="or-tabs__icon">{optionIcon}</span> : null}
                <span className="or-tabs__text">{getOptionLabel(option)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {hasError && errorText ? (
        <p className="or-tabs__message or-tabs__message--error" id={messageId}>
          {errorText}
        </p>
      ) : null}
      {!hasError && helperText ? (
        <p className="or-tabs__message" id={messageId}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
